import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Disc3, Calendar, Clock } from 'lucide-react';
import { differenceInDays } from 'date-fns';

export function FeaturedRelease() {
  const { data: release } = useQuery({
    queryKey: ['featured-release'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('releases')
        .select('id, title, release_type, genre, release_date, cover_art_url, status, org_id')
        .eq('featured', true)
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  if (!release) return null;

  const daysUntil = release.release_date
    ? differenceInDays(new Date(release.release_date), new Date())
    : null;

  return (
    <section className="py-20 bg-card/50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <p className="text-sm font-medium text-accent uppercase tracking-wider mb-2">Featured Release</p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground">Now Spinning</h2>
        </div>
        <Card className="max-w-lg mx-auto overflow-hidden border-border/50">
          {release.cover_art_url ? (
            <div className="aspect-square w-full overflow-hidden">
              <img src={release.cover_art_url} alt={release.title} className="h-full w-full object-cover" />
            </div>
          ) : (
            <div className="aspect-square w-full bg-card flex items-center justify-center">
              <Disc3 className="h-24 w-24 text-muted-foreground/30" />
            </div>
          )}
          <CardContent className="p-6 text-center space-y-2">
            <h3 className="text-xl font-bold text-foreground">{release.title}</h3>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <span className="capitalize">{release.release_type}</span>
              {release.genre && <span>· {release.genre}</span>}
            </div>
            {release.release_date && (
              <div className="flex items-center justify-center gap-2 text-sm">
                <Calendar className="h-3 w-3 text-accent" />
                <span className="text-muted-foreground">{new Date(release.release_date).toLocaleDateString()}</span>
                {daysUntil !== null && daysUntil > 0 && (
                  <Badge className="text-xs bg-accent/20 text-accent border-accent/30">
                    <Clock className="h-3 w-3 mr-1" />{daysUntil} days
                  </Badge>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
